import { html, LitElement } from "lit";
import { property } from "lit/decorators/property.js";
import { state } from "lit/decorators.js";

export class AnimatedCounter extends LitElement {
  @property({ type: Number }) finalValue = 0;
  @property({ type: Number }) duration = 1500;
  @property({ type: String }) scrollTarget?: string;

  @state() protected counter = 0;
  private intervalId = 0;

  connectedCallback() {
    super.connectedCallback();
    const userPrefersReducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (this.duration === 0 || userPrefersReducedMotion) {
      this.counter = this.finalValue;
    } else {
      this.observeScrollTarget();
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.unobserveScrollTarget();
    clearInterval(this.intervalId);
  }

  private handleCounterAnimateIfInView: IntersectionObserverCallback = (entries) => {
    if (entries.some((entry) => entry.isIntersecting)) {
      this.animateCounter();
      this.unobserveScrollTarget();
    }
  };

  private animateCounter = () => {
    const ANIMATION_FRAME_RATE = 24;
    const MS_PER_FRAME = 1000 / ANIMATION_FRAME_RATE;
    const INCREMENT_RATE_PER_FRAME = MS_PER_FRAME / this.duration;
    const COUNTER_INCREMENT_PER_FRAME = INCREMENT_RATE_PER_FRAME * this.finalValue;

    this.intervalId = setInterval(() => {
      const result = this.counter + COUNTER_INCREMENT_PER_FRAME;
      if (result >= this.finalValue) {
        this.counter = this.finalValue;
        clearInterval(this.intervalId);
        return;
      } else {
        this.counter = result;
      }
    }, MS_PER_FRAME);
  };

  private observer = new IntersectionObserver(this.handleCounterAnimateIfInView, {
    threshold: 0.1,
  });

  private observeScrollTarget = () => {
    const target = this.scrollTargetElement;
    target && this.observer.observe(target);
  };

  private unobserveScrollTarget = () => {
    const target = this.scrollTargetElement;
    target && this.observer.unobserve(target);
  };

  get scrollTargetElement(): this | Element | null {
    if (this.scrollTarget) {
      return document.querySelector(this.scrollTarget);
    } else {
      return this;
    }
  }

  render() {
    return html`${Math.ceil(this.counter).toLocaleString()}`;
  }
}

customElements.get("animated-counter") || customElements.define("animated-counter", AnimatedCounter);

declare global {
  interface HTMLElementTagNameMap {
    "animated-counter": AnimatedCounter;
  }
}
